import JSZip, { type JSZipObject } from 'jszip';
import XLSX from 'xlsx-js-style';

import {
  XlsxParseLimitError,
  type XlsxParseLimits,
} from './types';
import {
  parseRelationships,
  parseXmlAttributes,
  relationshipPartPath,
  resolveZipTarget,
} from './xml';

interface ZipObjectWithSize extends JSZipObject {
  _data?: {
    compressedSize?: number;
    uncompressedSize?: number;
  };
}

const XML_DECLARATION_RE = /<!DOCTYPE|<!ENTITY/i;

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) return;
  const error = new Error('XLSX parsing was aborted.');
  error.name = 'AbortError';
  throw error;
}

function byteLength(text: string): number {
  return new TextEncoder().encode(text).byteLength;
}

function objectSize(file: JSZipObject): number | undefined {
  const value = (file as ZipObjectWithSize)._data?.uncompressedSize;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function partSpecificLimit(path: string, limits: XlsxParseLimits): [number, XlsxParseLimitError['code']] {
  if (path === 'xl/sharedStrings.xml') return [limits.maxSharedStringsBytes, 'shared_strings_bytes'];
  if (path === 'xl/styles.xml') return [limits.maxStylesBytes, 'styles_bytes'];
  return [limits.maxXmlEntryBytes, 'xml_entry_bytes'];
}

export class OoxmlPackageReader {
  private readonly textCache = new Map<string, Promise<string>>();
  private worksheetPathPromise: Promise<string[]> | null = null;

  constructor(
    readonly zip: JSZip,
    readonly limits: XlsxParseLimits,
    readonly signal?: AbortSignal,
  ) {}

  has(path: string): boolean {
    return this.zip.file(path) != null;
  }

  async readText(path: string): Promise<string | null> {
    throwIfAborted(this.signal);
    const file = this.zip.file(path);
    if (!file) return null;
    let pending = this.textCache.get(path);
    if (!pending) {
      pending = (async () => {
        const [limit, code] = partSpecificLimit(path, this.limits);
        const knownSize = objectSize(file);
        if (knownSize !== undefined && knownSize > limit) {
          throw new XlsxParseLimitError(code, `XLSX part is too large: ${path} (${knownSize} bytes).`);
        }
        const text = await file.async('text');
        throwIfAborted(this.signal);
        const actualSize = byteLength(text);
        if (actualSize > limit) {
          throw new XlsxParseLimitError(code, `XLSX part is too large: ${path} (${actualSize} bytes).`);
        }
        if (XML_DECLARATION_RE.test(text)) {
          throw new XlsxParseLimitError(
            'xml_declaration',
            `XLSX XML declarations are not allowed in ${path}.`,
          );
        }
        return text;
      })();
      this.textCache.set(path, pending);
    }
    return pending;
  }

  async readBase64(path: string): Promise<string | null> {
    throwIfAborted(this.signal);
    const file = this.zip.file(path);
    if (!file) return null;
    const value = await file.async('base64');
    throwIfAborted(this.signal);
    return value;
  }

  async worksheetPaths(): Promise<string[]> {
    if (!this.worksheetPathPromise) this.worksheetPathPromise = this.resolveWorksheetPaths();
    return this.worksheetPathPromise;
  }

  private async resolveWorksheetPaths(): Promise<string[]> {
    const workbookPath = 'xl/workbook.xml';
    const workbookXml = await this.readText(workbookPath);
    const relsXml = await this.readText(relationshipPartPath(workbookPath));
    const paths: string[] = [];
    if (workbookXml && relsXml) {
      const relationships = parseRelationships(relsXml);
      const sheetRe = /<(?:\w+:)?sheet\b([^>]*?)\/?\s*>/gi;
      let match: RegExpExecArray | null;
      while ((match = sheetRe.exec(workbookXml)) !== null) {
        const attrs = parseXmlAttributes(match[1] ?? '');
        const relationshipId = attrs['r:id'] ?? attrs['relationships:id'] ?? attrs.id;
        if (!relationshipId) continue;
        const relationship = relationships.get(relationshipId);
        if (!relationship || !relationship.type.includes('/worksheet')) continue;
        const resolved = resolveZipTarget(workbookPath, relationship.target);
        if (resolved && this.has(resolved)) paths.push(resolved);
      }
    }
    if (paths.length > 0) return paths;
    return Object.keys(this.zip.files)
      .filter((path) => /^xl\/worksheets\/[^/]+\.xml$/i.test(path) && !this.zip.files[path].dir)
      .sort((left, right) => {
        const leftNumber = Number(/sheet(\d+)\.xml$/i.exec(left)?.[1] ?? Number.MAX_SAFE_INTEGER);
        const rightNumber = Number(/sheet(\d+)\.xml$/i.exec(right)?.[1] ?? Number.MAX_SAFE_INTEGER);
        return leftNumber - rightNumber || left.localeCompare(right);
      });
  }
}

export async function loadAndPreflightOoxmlPackage(
  source: Uint8Array,
  limits: XlsxParseLimits,
  signal?: AbortSignal,
): Promise<OoxmlPackageReader | null> {
  throwIfAborted(signal);
  const isZip =
    source.length >= 4 && source[0] === 0x50 && source[1] === 0x4b &&
    ((source[2] === 0x03 && source[3] === 0x04) ||
      (source[2] === 0x05 && source[3] === 0x06) ||
      (source[2] === 0x07 && source[3] === 0x08));
  if (!isZip) return null;

  const zip = await JSZip.loadAsync(source);
  throwIfAborted(signal);
  const entries = Object.values(zip.files);
  if (entries.length > limits.maxArchiveEntries) {
    throw new XlsxParseLimitError(
      'archive_entries',
      `XLSX archive has too many entries (${entries.length} > ${limits.maxArchiveEntries}).`,
    );
  }
  let expandedBytes = 0;
  for (const entry of entries) {
    const size = objectSize(entry);
    if (size !== undefined) expandedBytes += Math.max(0, size);
  }
  if (expandedBytes > limits.maxUncompressedBytes) {
    throw new XlsxParseLimitError(
      'archive_uncompressed_bytes',
      `XLSX expanded size is too large (${expandedBytes} > ${limits.maxUncompressedBytes}).`,
    );
  }

  const reader = new OoxmlPackageReader(zip, limits, signal);
  const worksheetPaths = Object.keys(zip.files).filter(
    (path) => /^xl\/worksheets\/[^/]+\.xml$/i.test(path) && !zip.files[path].dir,
  );
  if (worksheetPaths.length > limits.maxSheets) {
    throw new XlsxParseLimitError(
      'sheets',
      `XLSX has too many worksheets (${worksheetPaths.length} > ${limits.maxSheets}).`,
    );
  }

  let materializedCells = 0;
  for (const path of worksheetPaths) {
    throwIfAborted(signal);
    const xml = await reader.readText(path);
    if (!xml) continue;
    const dimensionTag = /<(?:\w+:)?dimension\b([^>]*?)\/?\s*>/i.exec(xml);
    const dimensionRef = dimensionTag ? parseXmlAttributes(dimensionTag[1] ?? '').ref : undefined;
    if (dimensionRef) {
      try {
        const range = XLSX.utils.decode_range(dimensionRef.replace(/\$/g, '').toUpperCase());
        if (range.e.r + 1 > limits.maxRows) {
          throw new XlsxParseLimitError('rows', `XLSX worksheet range exceeds ${limits.maxRows} rows: ${path}.`);
        }
        if (range.e.c + 1 > limits.maxColumns) {
          throw new XlsxParseLimitError('columns', `XLSX worksheet range exceeds ${limits.maxColumns} columns: ${path}.`);
        }
      } catch (error) {
        if (error instanceof XlsxParseLimitError) throw error;
        throw new Error(`Invalid XLSX worksheet dimension in ${path}: ${dimensionRef}`);
      }
    }

    const cellRe = /<(?:\w+:)?c\b([^>]*)>/gi;
    let match: RegExpExecArray | null;
    while ((match = cellRe.exec(xml)) !== null) {
      materializedCells += 1;
      if (materializedCells > limits.maxMaterializedCells) {
        throw new XlsxParseLimitError(
          'materialized_cells',
          `XLSX has too many materialized cells (${materializedCells} > ${limits.maxMaterializedCells}).`,
        );
      }
      const ref = parseXmlAttributes(match[1] ?? '').r;
      if (!ref) throw new Error(`XLSX cell is missing its coordinate in ${path}.`);
      try {
        const cell = XLSX.utils.decode_cell(ref.replace(/\$/g, '').toUpperCase());
        if (cell.r + 1 > limits.maxRows) {
          throw new XlsxParseLimitError('rows', `XLSX cell exceeds ${limits.maxRows} rows: ${ref}.`);
        }
        if (cell.c + 1 > limits.maxColumns) {
          throw new XlsxParseLimitError('columns', `XLSX cell exceeds ${limits.maxColumns} columns: ${ref}.`);
        }
      } catch (error) {
        if (error instanceof XlsxParseLimitError) throw error;
        throw new Error(`Invalid XLSX cell coordinate in ${path}: ${ref}`);
      }
    }
  }
  // Force size/declaration validation for the most expensive shared parts before SheetJS reads them.
  await reader.readText('xl/styles.xml');
  await reader.readText('xl/sharedStrings.xml');
  return reader;
}
